import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { paymentBatchApi, invoiceApi } from '../api';
import { PageHeader, Card, Button, LoadingSpinner, StatsCard, Modal } from '../components/UI';
import * as I from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PaymentBatchDetailsModal = ({ batch, onClose, onUpdate }) => {
    const { user } = useAuth();
    const [downloading, setDownloading] = useState(false);
    const [markingPaid, setMarkingPaid] = useState(false);

    // Detailed Payment Details
    const [showPayForm, setShowPayForm] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState({
        paymentMethod: 'NEFT',
        bulkPaymentReference: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paidBy: user?.name || 'Admin'
    });
    const [invoiceRefs, setInvoiceRefs] = useState({});

    useEffect(() => {
        if (batch?.id) {
            const fetchInvoices = async () => {
                setLoadingInvoices(true);
                try {
                    const response = await invoiceApi.getInvoicesByPaymentBatch(batch.id);
                    setInvoices(response.data || []);
                    // Initialize individual refs with empty strings
                    const refs = {};
                    (response.data || []).forEach(inv => {
                        refs[inv.id] = '';
                    });
                    setInvoiceRefs(refs);
                } catch (err) {
                    console.error('Failed to fetch batch invoices', err);
                    toast.error('Failed to load invoices for this batch');
                } finally {
                    setLoadingInvoices(false);
                }
            };
            fetchInvoices();
        }
    }, [batch?.id]);

    const handleDownload = async (type) => {
        try {
            setDownloading(true);
            const response = await paymentBatchApi.download(batch.id, type);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const ext = type === 'csv' ? 'csv' : type === 'txt' ? 'txt' : 'xlsx';
            link.setAttribute('download', `${batch.batchNumber}.${ext}`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error('Download failed', err);
        } finally {
            setDownloading(false);
        }
    };

    const handleMarkAsPaid = async () => {
        if (!paymentDetails.paymentMethod || !paymentDetails.bulkPaymentReference) {
            toast.warning('Please enter Payment Method and Bulk Reference');
            return;
        }

        try {
            setMarkingPaid(true);
            const payload = {
                ...paymentDetails,
                invoiceReferences: Object.entries(invoiceRefs).map(([id, ref]) => ({
                    invoiceId: parseInt(id),
                    paymentReference: ref
                }))
            };
            await paymentBatchApi.markAsPaid(batch.id, payload);
            toast.success('Batch marked as PAID successfully');
            onUpdate();
            onClose();
        } catch (err) {
            console.error('Mark as paid failed', err);
            toast.error(err.message || 'Failed to mark batch as paid');
        } finally {
            setMarkingPaid(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to DELETE this batch? Invoices will be released back to the payments pool.')) return;
        try {
            await paymentBatchApi.deleteBatch(batch.id);
            toast.success('Batch deleted successfully');
            onUpdate();
            onClose();
        } catch (err) {
            console.error('Delete batch failed', err);
            toast.error('Failed to delete batch');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
    };

    return (
        <Modal isOpen={true} onClose={onClose} title={`Batch: ${batch.batchNumber}`} maxWidth="4xl">
            <div className="space-y-6">
                {/* Status and Info Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm bg-gray-50 p-3 rounded-lg border">
                    <div>
                        <span className="text-gray-500 block">Status</span>
                        <span className={`font-semibold inline-flex items-center ${batch.status === 'PAID' ? 'text-green-600' : 'text-yellow-600'}`}>
                            {batch.status === 'PAID' ? <I.CheckCircle2 className="w-4 h-4 mr-1" /> : <I.Clock className="w-4 h-4 mr-1" />}
                            {batch.status}
                        </span>
                    </div>
                    <div>
                        <span className="text-gray-500 block">Total Amount</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(batch.totalAmount)}</span>
                    </div>
                    <div>
                        <span className="text-gray-500 block">Invoices</span>
                        <span className="font-medium">{batch.paymentCount}</span>
                    </div>
                    <div>
                        <span className="text-gray-500 block">Created On</span>
                        <span className="font-medium">{new Date(batch.createdDate).toLocaleDateString()}</span>
                    </div>
                </div>

                {!showPayForm && batch.status !== 'PAID' ? (
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-3">
                            <Button variant="outline" onClick={() => handleDownload('excel')} disabled={downloading}>
                                {downloading ? <LoadingSpinner size="sm" className="mr-2" /> : <I.FileSpreadsheet className="w-4 h-4 mr-2" />}
                                Export Excel
                            </Button>
                            <Button variant="outline" onClick={() => handleDownload('csv')} disabled={downloading}>
                                {downloading ? <LoadingSpinner size="sm" className="mr-2" /> : <I.FileText className="w-4 h-4 mr-2" />}
                                Export CSV
                            </Button>
                            <Button variant="outline" onClick={() => handleDownload('txt')} disabled={downloading}>
                                {downloading ? <LoadingSpinner size="sm" className="mr-2" /> : <I.FileType className="w-4 h-4 mr-2" />}
                                Export TXT
                            </Button>
                        </div>

                        <div className="pt-4 border-t flex gap-4">
                            <Button variant="primary" onClick={() => setShowPayForm(true)}>
                                <I.CreditCard className="w-4 h-4 mr-2" /> Mark as Paid
                            </Button>
                            <Button variant="outline" onClick={handleDelete} className="text-red-600 border-red-200 hover:bg-red-50">
                                <I.Trash2 className="w-4 h-4 mr-2" /> Delete Batch
                            </Button>
                        </div>
                    </div>
                ) : batch.status === 'PAID' ? (
                    <div className="grid grid-cols-2 gap-4 text-sm bg-green-50 p-4 rounded-lg border border-green-100">
                        <div>
                            <span className="text-gray-500 block">Paid By</span>
                            <span className="font-semibold text-gray-900">{batch.paidBy}</span>
                        </div>
                        <div>
                            <span className="text-gray-500 block">Paid Date</span>
                            <span className="font-semibold text-gray-900">{new Date(batch.paidDate).toLocaleString()}</span>
                        </div>
                        <div>
                            <span className="text-gray-500 block">Payment Method</span>
                            <span className="font-semibold text-gray-900">{batch.paymentMethod || 'N/A'}</span>
                        </div>
                        <div>
                            <span className="text-gray-500 block">Bulk Ref Number</span>
                            <span className="font-semibold text-gray-900">{batch.paymentReference || 'N/A'}</span>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4 border rounded-lg p-4 bg-white shadow-sm">
                        <h4 className="font-bold text-gray-800 flex items-center">
                            <I.Info className="w-4 h-4 mr-2 text-blue-500" /> Payment Details
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Payment Date</label>
                                <input
                                    type="date"
                                    className="w-full text-sm border-gray-300 rounded focus:ring-blue-500"
                                    value={paymentDetails.paymentDate}
                                    onChange={e => setPaymentDetails({ ...paymentDetails, paymentDate: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Payment Method</label>
                                <select
                                    className="w-full text-sm border-gray-300 rounded focus:ring-blue-500"
                                    value={paymentDetails.paymentMethod}
                                    onChange={e => setPaymentDetails({ ...paymentDetails, paymentMethod: e.target.value })}
                                >
                                    <option value="NEFT">NEFT</option>
                                    <option value="RTGS">RTGS</option>
                                    <option value="CHEQUE">CHEQUE</option>
                                    <option value="ONLINE">ONLINE</option>
                                    <option value="OTHER">OTHER</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Bulk Reference No.</label>
                                <input
                                    type="text"
                                    placeholder="Enter Bulk UTR / Ref"
                                    className="w-full text-sm border-gray-300 rounded focus:ring-blue-500"
                                    value={paymentDetails.bulkPaymentReference}
                                    onChange={e => setPaymentDetails({ ...paymentDetails, bulkPaymentReference: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="mt-6">
                            <h5 className="text-xs font-bold text-gray-600 uppercase mb-2 border-b pb-1">Individual Invoice References (Optional)</h5>
                            <div className="max-h-60 overflow-y-auto border rounded divide-y">
                                {loadingInvoices ? (
                                    <div className="p-4 text-center"><LoadingSpinner size="sm" /></div>
                                ) : invoices.map(inv => (
                                    <div key={inv.id} className="p-2 grid grid-cols-12 items-center gap-2 hover:bg-gray-50">
                                        <div className="col-span-3">
                                            <p className="text-xs font-bold text-gray-900">{inv.invoiceNumber}</p>
                                            <p className="text-[10px] text-gray-500 truncate">{inv.vendorName}</p>
                                        </div>
                                        <div className="col-span-3 text-right">
                                            <p className="text-xs font-medium text-gray-900">{formatCurrency(inv.invoicePayable)}</p>
                                        </div>
                                        <div className="col-span-6">
                                            <input
                                                type="text"
                                                placeholder="Unique Invoice Ref (Optional)"
                                                className="w-full text-[11px] py-1 border-gray-200 rounded focus:ring-blue-500"
                                                value={invoiceRefs[inv.id] || ''}
                                                onChange={e => setInvoiceRefs({ ...invoiceRefs, [inv.id]: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1 italic">* If individual ref is empty, Bulk Ref will be used for that invoice.</p>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t">
                            <Button variant="secondary" onClick={() => setShowPayForm(false)}>Back</Button>
                            <Button variant="primary" onClick={handleMarkAsPaid} disabled={markingPaid}>
                                {markingPaid ? 'Processing...' : 'Confirm & Mark Paid'}
                            </Button>
                        </div>
                    </div>
                )}

                <div className="flex justify-end pt-2">
                    {!showPayForm && <Button variant="secondary" onClick={onClose}>Close</Button>}
                </div>
            </div>
        </Modal>
    );
};

const PaymentBatchesPage = () => {
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchBatches = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await paymentBatchApi.getAllBatches();
            // Sort desc by id
            const sorted = (resp.data || []).sort((a, b) => b.id - a.id);
            setBatches(sorted);
        } catch (err) {
            console.error('Error fetching payment batches', err);
            toast.error('Failed to load payment batches');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBatches();
    }, [fetchBatches]);

    const handleCreateBatch = () => {
        // For now, this is triggered from Payments Page, not here.
        // This page is just for viewing/managing batches.
    };

    const openBatchDetails = (batch) => {
        setSelectedBatch(batch);
        setIsModalOpen(true);
    };

    const closeBatchDetails = () => {
        setSelectedBatch(null);
        setIsModalOpen(false);
        fetchBatches(); // Refresh on close in case status exchanged
    };

    const formatCurrency = (amount) => {
        if (amount == null) return '-';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleString('en-IN');
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 space-y-4 bg-gray-50 min-h-screen">
            <PageHeader
                title="Payment Batches"
                subtitle="Manage bulk payment batches and downloads"
                actions={[
                    <Button key="refresh" variant="outline" onClick={fetchBatches}>
                        <I.RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                ]}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <StatsCard title="Total Batches" value={batches.length} icon={I.Layers} />
                <StatsCard title="Total Amount (Pending)" value={formatCurrency(batches.filter(b => b.status !== 'PAID').reduce((s, b) => s + (b.totalAmount || 0), 0))} icon={I.IndianRupee} />
            </div>

            <Card>
                {loading ? (
                    <div className="flex justify-center p-8"><LoadingSpinner /></div>
                ) : batches.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No payment batches found. Create one from the Payments page.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Batch #</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Created Date</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Created By</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Count</th>
                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Total Amount</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Action</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {batches.map(batch => (
                                    <tr key={batch.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{batch.batchNumber}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(batch.createdDate)}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500">{batch.createdBy}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500">{batch.paymentCount}</td>
                                        <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">{formatCurrency(batch.totalAmount)}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${batch.status === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                {batch.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right text-sm font-medium">
                                            <Button size="sm" variant="secondary" onClick={() => openBatchDetails(batch)}>
                                                View
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {isModalOpen && selectedBatch && (
                <PaymentBatchDetailsModal
                    batch={selectedBatch}
                    onClose={closeBatchDetails}
                    onUpdate={fetchBatches}
                />
            )}
        </div>
    );
};

export default PaymentBatchesPage;
